import { Component, EventEmitter, Input, OnInit, Output, forwardRef, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';

@Component({
    selector: 'app-input-otp',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './app-input-otp.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AppInputOtp),
            multi: true
        }
    ]
})
export class AppInputOtp implements ControlValueAccessor, OnInit {
    @Input() label: string = '';
    @Input() length: number = 6;
    @Input() disabled: boolean = false;
    @Input() error: string = '';
    @Input() size: 'xs' | 'sm' | 'md' | 'lg' = 'md';
    @Input() fullWidth: boolean = false;
    @Output() valueChange = new EventEmitter<string>();

    @ViewChildren('otpInput') inputs!: QueryList<ElementRef<HTMLInputElement>>;

    otpValues: string[] = [];

    onChange: any = () => { };
    onTouched: any = () => { };

    ngOnInit(): void {
        this.otpValues = Array(this.length).fill('');
    }

    writeValue(value: any): void {
        if (value) {
            const digits = value.toString().slice(0, this.length).split('');
            this.otpValues = Array(this.length).fill('').map((_, i) => digits[i] || '');
        } else {
            this.otpValues = Array(this.length).fill('');
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    trackByFn(index: number) {
        return index;
    }

    onFocus(event: any) {
        const input = event.target as HTMLInputElement;
        input.select();
    }

    onKeyDown(event: KeyboardEvent, index: number) {
        // Manejo de números
        if (/^[0-9]$/.test(event.key)) {
            event.preventDefault();
            this.otpValues[index] = event.key;
            this.emitValue();
            
            if (index < this.length - 1) {
                const nextInput = this.inputs.toArray()[index + 1]?.nativeElement;
                nextInput?.focus();
            }
            return;
        }

        // Manejo de borrado
        if (event.key === 'Backspace') {
            event.preventDefault();
            
            if (this.otpValues[index]) {
                this.otpValues[index] = '';
            } else if (index > 0) {
                this.otpValues[index - 1] = '';
                const prevInput = this.inputs.toArray()[index - 1]?.nativeElement;
                prevInput?.focus();
            }
            this.emitValue();
            return;
        }

        // Permitir Tab, flechas, etc.
        if (['Tab', 'ArrowLeft', 'ArrowRight', 'Delete'].includes(event.key)) {
            return;
        }

        // Bloquear cualquier otra tecla
        event.preventDefault();
    }

    onPaste(event: ClipboardEvent) {
        event.preventDefault();
        const pastedData = event.clipboardData?.getData('text') || '';
        const digits = pastedData.replace(/\D/g, '').slice(0, this.length).split('');

        digits.forEach((digit, i) => {
            if (i < this.length) this.otpValues[i] = digit;
        });

        const lastIdx = Math.min(digits.length, this.length - 1);
        const lastInput = this.inputs.toArray()[lastIdx]?.nativeElement;
        lastInput?.focus();

        this.emitValue();
    }

    private emitValue() {
        const fullValue = this.otpValues.join('');
        this.onChange(fullValue);
        this.onTouched();
        this.valueChange.emit(fullValue);
    }

    get sizeClasses(): string {
        const map: Record<string, string> = {
            xs: 'w-10 h-14 text-xl',
            sm: 'w-12 h-16 text-2xl',
            md: 'w-14 h-20 text-3xl',
            lg: 'w-16 h-24 text-4xl',
        };
        return map[this.size] ?? map['md'];
    }

    get gapClass(): string {
        const map: Record<string, string> = {
            xs: 'gap-2',
            sm: 'gap-3',
            md: 'gap-4',
            lg: 'gap-5',
        };
        return map[this.size] ?? map['md'];
    }

    get distributeClass(): string {
        if (this.fullWidth) return 'justify-between w-full';
        return `justify-start ${this.gapClass}`;
    }

    /** Foco programático al primer campo */
    focusFirst() {
        setTimeout(() => {
            const firstInput = this.inputs.toArray()[0]?.nativeElement;
            firstInput?.focus();
        }, 10);
    }

    /** Limpia el valor y pone foco */
    clear() {
        this.writeValue('');
        this.focusFirst();
    }
}
